import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Api from '../../api/reconApi';
import {
	backfillButtonEnabled,
	backfillIsRunning,
	backfillProgressBar,
	backfillView,
	canSeeReconFileBackfill,
} from '../../api/reconFileBackfill';
import { fetchReconFileBackfill, startReconFileBackfill } from '../../api/reconFileBackfillClient';
import '../../inputs/FileUploadButton/uploadProgress.scss';
import './ReconFileBackfill.scss';

const POLL_MS = 3000;

function countText(status, emphasizeFailed) {
	const migrated = Number(status && status.migrated) || 0;
	const failed = Number(status && status.failed) || 0;
	const pending = Number(status && status.pending) || 0;
	const failedText = emphasizeFailed
		? <strong className="text-danger">{failed} failed</strong>
		: <span>{failed} failed</span>;
	return (
		<p>
			{migrated}
			{' '}
			migrated.
			{' '}
			{failedText}
			.
			{' '}
			{pending}
			{' '}
			pending.
		</p>
	);
}

function ReconFileBackfill({ position }) {
	const [status, setStatus] = useState(null);
	const [problem, setProblem] = useState('');
	const [starting, setStarting] = useState(false);
	const timer = useRef(null);
	const applyRef = useRef(() => {});

	const stop = () => {
		if (timer.current) {
			clearInterval(timer.current);
			timer.current = null;
		}
	};

	const onError = useCallback((error) => {
		const code = error && error.response && error.response.status;
		if (code === 401 || code === 403) {
			if (timer.current) {
				clearInterval(timer.current);
				timer.current = null;
			}
		}
		const result = Api.handleApiError(error, 'recon_file_backfill');
		const message = result && result.errors && result.errors.recon_file_backfill;
		setProblem(message || 'There was an error');
	}, []);

	applyRef.current = (next) => {
		setStatus(next);
		setProblem('');
		if (backfillIsRunning(next && next.state)) {
			if (!timer.current) {
				timer.current = setInterval(() => {
					fetchReconFileBackfill().then(latest => applyRef.current(latest)).catch(onError);
				}, POLL_MS);
			}
			return;
		}
		stop();
	};

	useEffect(() => {
		if (!canSeeReconFileBackfill(position)) return undefined;
		let cancelled = false;
		fetchReconFileBackfill().then((next) => {
			if (!cancelled) applyRef.current(next);
		}).catch((error) => {
			if (!cancelled) onError(error);
		});
		return () => {
			cancelled = true;
			stop();
		};
	}, [onError, position]);

	if (!canSeeReconFileBackfill(position)) return null;
	if (!status) {
		return problem ? (
			<div className="row">
				<div className="col-md-12">
					<p className="text-danger">{problem}</p>
				</div>
			</div>
		) : null;
	}

	const view = backfillView(status);
	const progress = backfillProgressBar(status);
	const enabled = !!status && backfillButtonEnabled(status.state) && !starting;

	function startCopy() {
		setStarting(true);
		startReconFileBackfill().then((next) => {
			applyRef.current(next);
		}).catch(onError).finally(() => setStarting(false));
	}

	return (
		<div className="row">
			<div className="col-md-12">
				<div className="recon-file-backfill-status">
					{view.title ? <p>{view.title}</p> : null}
					{view.counts ? countText(status, view.emphasizeFailed) : null}
					{progress ? (
						<div
							className={progress.indeterminate ? 'upload-progress is-indeterminate' : 'upload-progress'}
							role="progressbar"
							aria-valuemin={0}
							aria-valuemax={100}
							aria-valuenow={progress.indeterminate ? undefined : progress.percent}
							aria-valuetext={progress.indeterminate ? 'Working' : `${progress.percent}%`}
						>
							<div
								className="upload-progress-fill"
								style={progress.indeterminate ? undefined : { width: `${progress.percent}%` }}
							/>
							{progress.indeterminate ? null : (
								<div className="upload-progress-label">{`${progress.percent}%`}</div>
							)}
						</div>
					) : null}
					{view.detail ? <p className={view.detailDanger ? 'text-danger' : undefined}>{view.detail}</p> : null}
				</div>
				{problem ? <p className="text-danger">{problem}</p> : null}
				<button
					type="button"
					className="btn btn-success"
					disabled={!enabled}
					onClick={startCopy}
				>
					Copy files to S3
				</button>
			</div>
		</div>
	);
}

function mapStateToProps(state) {
	return {
		position: state.current_user && state.current_user.position,
	};
}

export default connect(mapStateToProps)(ReconFileBackfill);
